'use client';

import { useEffect } from 'react';

type TypekitFamily = 'futura' | 'dharma';

const KIT_URLS: Record<TypekitFamily, string> = {
  futura: 'https://use.typekit.net/ibm8mvt.css',
  dharma: 'https://use.typekit.net/xaw6xmn.css',
};

// Adobe serves these faces with font-display:auto (FOIT), so on slow mobile
// the headline is invisible until the third-party font arrives. We probe each
// kit's font with the CSS Font Loading API and flip a `<kit>-ready` class on
// <html> only once the glyphs are downloaded; the dharma-titled components
// render a visible condensed fallback until then (see `html.dharma-ready &`).
const KIT_PROBES: Partial<Record<TypekitFamily, string[]>> = {
  // gothic-m → `dharma-ready` (CampaignHeroBanner). Italic -e →
  // `dharma-e-ready` (`v3.campaignDisplay`). Do not let -m unlock -e.
  dharma: [
    '400 1em "dharma-gothic-e"',
    'italic 400 1em "dharma-gothic-e"',
    '400 1em "dharma-gothic-m"',
  ],
  futura: ['400 1em "futura-pt-condensed"', '700 1em "futura-pt-condensed"'],
};

type TypekitFontsProps = {
  kits: TypekitFamily[];
};

// React 19 hoists <link> elements rendered anywhere into <head> (Float),
// deduped via precedence — works under both routers, unlike next/head.
// Typekit stays a blocking stylesheet by design; see memory note on splash.
const TypekitFonts = ({ kits }: TypekitFontsProps) => {
  useEffect(() => {
    if (typeof document === 'undefined' || !('fonts' in document)) return;
    kits.forEach((kit) => {
      const probes = KIT_PROBES[kit];
      if (!probes) return;
      const families = probes
        .map((p) => p.match(/"([^"]+)"/)?.[1])
        .filter((f): f is string => Boolean(f));

      // `document.fonts.load()` REJECTS in Chromium for these kits, so the
      // previous Promise.all(...).catch() swallowed the failure and the class
      // never flipped — every `html.dharma-ready` rule was permanently stuck on
      // its fallback face. Wait for the font set to settle, then ask whether
      // any probed family actually resolved.
      const check = (spec: string) => {
        try {
          return document.fonts.check(spec);
        } catch {
          return false;
        }
      };

      const flip = () => {
        if (kit === 'dharma') {
          if (
            check('400 1em "dharma-gothic-m"') ||
            check('400 1em "dharma-gothic-e"') ||
            check('italic 400 1em "dharma-gothic-e"')
          ) {
            document.documentElement.classList.add('dharma-ready');
          }
          if (
            check('italic 400 1em "dharma-gothic-e"') ||
            check('400 1em "dharma-gothic-e"')
          ) {
            document.documentElement.classList.add('dharma-e-ready');
          }
          return;
        }

        const ok = families.some(
          (family) =>
            check(`400 1em "${family}"`) || check(`italic 400 1em "${family}"`),
        );
        if (ok) document.documentElement.classList.add(`${kit}-ready`);
      };

      Promise.allSettled(probes.map((p) => document.fonts.load(p)))
        .then(() => document.fonts.ready)
        .then(flip)
        .catch(flip);
    });
  }, [kits]);

  return (
    <>
      <link
        rel="preconnect"
        href="https://use.typekit.net"
        crossOrigin="anonymous"
      />
      <link
        rel="preconnect"
        href="https://p.typekit.net"
        crossOrigin="anonymous"
      />
      {kits.map((kit) => (
        <link
          key={`preload-${kit}`}
          rel="preload"
          as="style"
          href={KIT_URLS[kit]}
        />
      ))}
      {kits.map((kit) => (
        <link
          key={kit}
          rel="stylesheet"
          href={KIT_URLS[kit]}
          precedence="default"
        />
      ))}
    </>
  );
};

export default TypekitFonts;
